import { Link, useNavigate } from "@tanstack/react-router";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { Fragment } from "react/jsx-runtime";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Separator } from "~/components/ui/separator";
import { SidebarTrigger, useSidebar } from "~/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { type Crumb, useBreadcrumbs } from "~/hooks/use-breadcrumbs";
import { useImperativeDialogRef } from "~/hooks/use-imperative-dialog";
import { type NonEmptyArray, isNonEmptyArray } from "~/lib/util-types";
import { cn } from "~/lib/utils";
import { AddPageDialog } from "~/routes/(app)/pages/$pageId/-components/add-page-dialog";

type CrumbWithOptions = Omit<CrumbItemContentProps["crumb"], "options"> & {
  options: NonEmptyArray<CrumbItemContentProps["crumb"]["options"][number]>;
};

function hasCrumbOptions(crumb: Crumb): crumb is CrumbWithOptions {
  return isNonEmptyArray(crumb.options);
}

function CrumbOptions({ crumb }: { crumb: CrumbWithOptions }) {
  const nav = useNavigate();

  const linkOptions = crumb.options.filter((o) => o.type === "nav");
  const actions = crumb.options.filter((o) => o.type == "action");

  const addPageDialogRef = useImperativeDialogRef();

  const [open, setOpen] = useState(false);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className="justify-between -mr-2 -ml-2 text-sm"
          >
            {crumb.label}
            <ChevronsUpDown />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder="Search pages..." className="h-9" />

            <CommandList className="!max-h-max">
              <CommandEmpty>No pages found.</CommandEmpty>
              <CommandGroup className="max-h-[392px] overflow-y-auto">
                {linkOptions.map((opt) => (
                  <CommandItem
                    key={opt.label}
                    value={opt.label}
                    onSelect={() => {
                      nav(opt);
                      requestAnimationFrame(() => setOpen(false));
                    }}
                  >
                    {opt.label}
                    <Check
                      className={cn(
                        "ml-auto",
                        opt.label === crumb.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>

              {actions.length ? <CommandSeparator /> : null}

              <CommandGroup>
                {actions.map((act, i) => (
                  <CommandItem
                    key={i}
                    onSelect={() =>
                      act.dialog === "add_page" &&
                      addPageDialogRef.current?.open()
                    }
                  >
                    {act.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <AddPageDialog ref={addPageDialogRef} />
    </>
  );
}

type CrumbItemContentProps = {
  isLast: boolean;
  crumb: ReturnType<typeof useBreadcrumbs>[number];
};

function CrumbItemContent({ isLast, crumb }: CrumbItemContentProps) {
  if (hasCrumbOptions(crumb)) {
    return <CrumbOptions crumb={crumb} />;
  }

  if (isLast) {
    return (
      <BreadcrumbPage>
        <h1 className="text-sm">{crumb.label}</h1>
      </BreadcrumbPage>
    );
  }

  return (
    <BreadcrumbLink asChild>
      <Link className="text-sm" to={crumb.to}>
        {crumb.label}
      </Link>
    </BreadcrumbLink>
  );
}

export function NavCrumbs() {
  const crumbs = useBreadcrumbs();
  const { open: isSidebarOpen } = useSidebar();

  return (
    <>
      <style>
        {`#nav-crumbs + * { grid-area: main; padding: 0 16px; overflow-y: auto; }`}
      </style>

      <header
        id="nav-crumbs"
        className="flex h-[52px] w-full shrink-0 items-center gap-2 pl-4"
        style={{ gridArea: "nav" }}
      >
        <Tooltip delayDuration={500}>
          <TooltipTrigger asChild>
            <SidebarTrigger className="hover:bg-accent -ml-1" />
          </TooltipTrigger>
          <TooltipContent>
            <p>{isSidebarOpen ? "Hide" : "Show"} sidebar</p>
          </TooltipContent>
        </Tooltip>
        {crumbs.length > 0 && (
          <Separator orientation="vertical" className="mr-1 h-4!" />
        )}
        <Breadcrumb className="w-full">
          <BreadcrumbList>
            {crumbs.map((crumb, index) => (
              <Fragment key={crumb.to}>
                <BreadcrumbItem>
                  <CrumbItemContent
                    crumb={crumb}
                    isLast={index === crumbs.length - 1}
                  />
                </BreadcrumbItem>
                {index < crumbs.length - 1 ? (
                  <BreadcrumbSeparator>/</BreadcrumbSeparator>
                ) : null}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </header>
    </>
  );
}
